import { RELATION_PART_OF } from '@backstage/catalog-model';
import { TableColumn } from '@backstage/core-components';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import {
  EntityRefLinks,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { makeStyles } from '@material-ui/core';
import { SystemCell } from '../service-group-columns/SystemCell';
import { NameCell } from '../catalog-index-page/table/NameCell';
import { OwnerCell } from '../catalog-index-page/table/OwnerCell';
import { displayedName } from '../catalog-index-page/table/displayed-name';
import { EmptyCell } from '../catalog-index-page/table/EmptyCell';
import { TagPills } from '../catalog-index-page/table/TagPills';
import { TruncatedText } from '../catalog-index-page/table/TruncatedText';

function getServiceGroupRelations(row: CatalogTableRow) {
  return getEntityRelations(row.entity, RELATION_PART_OF, {
    kind: 'servicegroup',
  });
}

// Links sit above the Name link's stretched ::after so they stay clickable.
const useStyles = makeStyles({
  link: { position: 'relative', zIndex: 1 },
});

function ServiceGroupCell({ row }: { row: CatalogTableRow }) {
  const classes = useStyles();
  const refs = getServiceGroupRelations(row);
  if (refs.length === 0) {
    return <EmptyCell />;
  }
  return (
    <TruncatedText>
      <span className={classes.link}>
        <EntityRefLinks entityRefs={refs} defaultKind="servicegroup" />
      </span>
    </TruncatedText>
  );
}

function SystemColumnCell({ row }: { row: CatalogTableRow }) {
  const classes = useStyles();
  return (
    <TruncatedText>
      <span className={classes.link}>
        <SystemCell entity={row.entity} emptyFallback={<EmptyCell />} />
      </span>
    </TruncatedText>
  );
}

// Widths are the mock's (28/14/14/15/8) scaled up so the actions column keeps
// about 9% instead of absorbing all the leftover space.
function createComponentColumns(): TableColumn<CatalogTableRow>[] {
  return [
    {
      title: 'Name',
      width: '32%',
      field: 'resolved.entityRef',
      highlight: true,
      // Sorts by the name NameCell shows.
      customSort: (a, b) =>
        displayedName(a.entity).localeCompare(displayedName(b.entity)),
      render: row => <NameCell entity={row.entity} />,
    },
    {
      title: 'System',
      width: '16%',
      render: row => <SystemColumnCell row={row} />,
    },
    {
      title: 'Service group',
      width: '16%',
      customFilterAndSearch: (query, row) => {
        const names = getServiceGroupRelations(row)
          .map(ref => ref.name)
          .join(', ')
          .toLocaleUpperCase('en-US');
        return names.includes(query.toLocaleUpperCase('en-US'));
      },
      render: row => <ServiceGroupCell row={row} />,
    },
    {
      title: 'Owner',
      width: '17%',
      field: 'resolved.ownedByRelationsTitle',
      render: row => <OwnerCell entity={row.entity} />,
    },
    {
      title: 'Tags',
      width: '10%',
      field: 'entity.metadata.tags',
      render: row => <TagPills tags={row.entity.metadata.tags} />,
    },
  ];
}

/**
 * Columns for the Catalog table. Components get our explicit Name, System,
 * Service group, Owner and Tags columns (System falls back through its
 * ServiceGroup). Every other kind reuses CatalogTable.defaultColumnsFunc
 * unchanged, so anything upstream changes about those kinds keeps working.
 */
export const catalogTableColumnsFunc: CatalogTableColumnsFunc =
  entityListContext => {
    const effectiveKind =
      entityListContext.entities[0]?.kind?.toLocaleLowerCase('en-US') ??
      entityListContext.filters.kind?.value;

    if (effectiveKind === 'component') {
      return createComponentColumns();
    }
    return CatalogTable.defaultColumnsFunc(entityListContext);
  };
